import { useState, useRef } from 'react';
import { projects } from '../data/portfolioData';

const ProjectCard = ({ project, offset, onClick }) => {
  const abs = Math.abs(offset);
  return (
    <div
      className="cf-card"
      style={{
        transform: `translateX(${offset * 220}px) translateZ(${-abs * 150}px) rotateY(${offset * -18}deg) scale(${abs === 0 ? 1 : abs === 1 ? 0.88 : 0.76})`,
        opacity: abs > 2 ? 0 : 1,
        zIndex: 10 - abs,
        filter: `blur(${abs === 0 ? 0 : abs === 1 ? 1 : 2}px)`,
        pointerEvents: abs > 1 ? 'none' : 'auto',
      }}
      onClick={onClick}
    >
      <div className="notch" />
      <div className="phone-screen" style={{ background: project.bg }}>
        <div className="scr-bar">
          <span>9:41</span>
          <span>{project.id.toUpperCase()}</span>
          <span>●●●</span>
        </div>
        <div className="scr-hero">
          <div className="sh-k">▸ {project.tag}</div>
          <div className="sh-v">{project.name}</div>
        </div>
        <div className="scr-body">
          <div className="scr-tile" style={{ background: project.accent }}>
            <div style={{ fontFamily: 'VT323,monospace', fontSize: '13px', letterSpacing: '2px', opacity: .8 }}>TODAY</div>
            <div className="scr-num">{project.stats[0][1]}</div>
            <div style={{ fontSize: '12px', opacity: .8 }}>{project.stats[0][0].toLowerCase()}</div>
          </div>
          <div className="scr-row">
            <div className="scr-tile" style={{ background: 'rgba(255,255,255,.85)' }}>
              <div style={{ fontFamily: 'VT323,monospace', fontSize: '12px', opacity: .6 }}>{project.stats[1][0].toUpperCase()}</div>
              <div className="scr-num" style={{ fontSize: '22px' }}>{project.stats[1][1]}</div>
            </div>
            <div className="scr-tile" style={{ background: 'rgba(255,255,255,.85)' }}>
              <div style={{ fontFamily: 'VT323,monospace', fontSize: '12px', opacity: .6 }}>{project.stats[2][0].toUpperCase()}</div>
              <div className="scr-num" style={{ fontSize: '22px' }}>{project.stats[2][1]}</div>
            </div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: 'auto' }}>
            {project.features.map((f) => (
              <span key={f} className="scr-chip">◇ {f}</span>
            ))}
          </div>
        </div>
        <div className="scr-nav">
          <div className="active" /><div /><div /><div />
        </div>
      </div>
    </div>
  );
};

const ProjectDetails = ({ project }) => (
  <div className="cf-details">
    <div className="cfd-head">
      <span className="cfd-tag">{project.tag}</span>
      <span className="cfd-year">{project.year}</span>
    </div>
    <h3 className="cfd-title">{project.name}</h3>
    <p className="cfd-sub">{project.subtitle}</p>

    <div className="cfd-grid">
      <div className="cfd-block">
        <div className="cfd-k">◆ CLIENT</div>
        <div className="cfd-v">{project.client}</div>
      </div>
      <div className="cfd-block">
        <div className="cfd-k">◆ MY ROLE</div>
        <div className="cfd-v">{project.role}</div>
      </div>
    </div>

    <div className="cfd-split">
      <div className="cfd-col">
        <div className="cfd-k">◆ THE STORY</div>
        <p className="cfd-story">{project.blurb}</p>
        <div className="cfd-k cfd-k2">◆ THE CHALLENGE</div>
        <p className="cfd-challenge">{project.challenge}</p>
      </div>
      <div className="cfd-col">
        <div className="cfd-k">◆ WHAT I SHIPPED</div>
        <ul className="cfd-did">
          {project.did.map((item) => <li key={item}>{item}</li>)}
        </ul>
        <div className="cfd-k cfd-k2">◆ HIGHLIGHTS</div>
        <div className="cfd-stats">
          {project.stats.map(([k, v]) => (
            <div key={k} className="cfd-stat">
              <b>{k.toUpperCase()}</b>
              <span>{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export const Projects = ({ isVisible }) => {
  const [idx, setIdx] = useState(0);
  const total = projects.length;
  const dragStart = useRef(null);

  const go = (i) => setIdx(((i % total) + total) % total);

  return (
    <section id="projects" className={`projects ${isVisible ? 'fade-in' : ''}`}>
      <div className="sec-head">
        <span className="kicker">◆ 03 · GREATEST HITS</span>
        <h2>Four apps, <em>shipped</em>.</h2>
      </div>

      <div className="coverflow">
        <div
          className="cf-stage"
          onPointerDown={(e) => { dragStart.current = e.clientX; }}
          onPointerUp={(e) => {
            if (dragStart.current == null) return;
            const delta = e.clientX - dragStart.current;
            if (Math.abs(delta) > 60) go(idx + (delta < 0 ? 1 : -1));
            dragStart.current = null;
          }}
        >
          {projects.map((p, j) => (
            <ProjectCard
              key={p.id}
              project={p}
              offset={j - idx}
              onClick={() => go(j)}
            />
          ))}
        </div>

        <div className="cf-controls">
          <button className="cf-btn" onClick={() => go(idx - 1)} aria-label="Previous">◀</button>
          <div className="cf-meta">
            <div className="cf-count">{idx + 1} / {total}</div>
            <div className="cf-name">{projects[idx].name}</div>
          </div>
          <button className="cf-btn" onClick={() => go(idx + 1)} aria-label="Next">▶</button>
        </div>

        <div className="cf-dots">
          {projects.map((_, j) => (
            <button
              key={j}
              className={`cf-dot ${j === idx ? 'active' : ''}`}
              onClick={() => go(j)}
              aria-label={`Project ${j + 1}`}
            />
          ))}
        </div>

        <ProjectDetails project={projects[idx]} />
      </div>
    </section>
  );
};
